// src/ImpersonationBanner.js
import React from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ReportProblemIcon from '@mui/icons-material/ReportProblem'; // Warning icon for the banner

function ImpersonationBanner({ isImpersonating, impersonatorName, targetUserName }) {
    const navigate = useNavigate();

    const handleEndImpersonation = () => {
        console.log("ImpersonationBanner: Ending impersonation...");
        const originalAdminToken = localStorage.getItem('original_admin_token');

        if (originalAdminToken) {
            localStorage.setItem('token', originalAdminToken); // Restore admin's original token as the active token
            console.log("ImpersonationBanner: Original admin token restored as active token.");
        } else {
            // Fallback if original token isn't found for some reason.
            // This would effectively log the user out as there's no valid token to restore.
            localStorage.removeItem('token'); // Clear the current (impersonation) token
            console.warn("ImpersonationBanner: Original admin token not found. Active token cleared. User will be logged out.");
        }

        // Clear all impersonation-specific flags and stored original admin details from localStorage
        localStorage.removeItem('original_admin_token');
        localStorage.removeItem('original_admin_userId');
        localStorage.removeItem('original_admin_userName');
        localStorage.removeItem('isImpersonating');
        
        // Dispatch 'authChanged' event. App.js will listen to this.
        // App.js's syncAuthState will then:
        // 1. Read the restored admin token (or lack thereof).
        // 2. Decode it and update its state (userId, userName, userRole, etc.) to the admin's details.
        // 3. Update localStorage items (userId, userName, etc.) based on the admin's token.
        // 4. Set isImpersonating state to false.
        window.dispatchEvent(new Event('authChanged'));
        
        // Navigate to a neutral/admin page. Dashboard is a common landing spot.
        navigate('/dashboard'); 
    };

    if (!isImpersonating) {
        return null; // Don't render anything if not impersonating
    }

    return (
        <Alert 
            severity="warning" 
            icon={<ReportProblemIcon fontSize="inherit" />}
            action={
                <Button 
                    color="inherit" 
                    size="small" 
                    onClick={handleEndImpersonation} 
                    variant="outlined"
                    sx={{ whiteSpace: 'nowrap' }} // Prevent button text from wrapping
                >
                    End Impersonation
                </Button>
            }
            sx={{
                position: 'sticky', // Sticks to the top when scrolling
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1301, // High z-index to be on top of other elements (e.g., MUI AppBar which is often 1100-1300)
                borderRadius: 0, // No rounded corners for a full-width banner
                display: 'flex',
                alignItems: 'center', // Vertically center content
                py: 0.5, // Reduced padding for a less tall banner
                '& .MuiAlert-message': { 
                    flexGrow: 1, 
                    textAlign: 'center',
                    mr: 1, // Margin between message and action button
                },
                '& .MuiAlert-action': {
                    mr: 1, // Ensure some space for the action button
                }
            }}
        >
            <Typography variant="body2" component="span" sx={{ textAlign: 'center', width: '100%'}}>
                Viewing as: <strong>{targetUserName || 'Target User'}</strong>.
                (Impersonated by: <strong>{impersonatorName || 'Admin'}</strong>).
                Actions taken are on behalf of the viewed user.
            </Typography>
        </Alert>
    );
}

export default ImpersonationBanner;
